import { useCallback } from "react";
import { User } from "@/lib/types";
import { subscribeToUsers } from "@/lib/firebase/services";
import { useSubscription, type Subscribe } from "./useSubscription";

interface UseUsersResult {
  users: User[];
  loading: boolean;
  error: Error | null;
}

const NO_USERS: User[] = [];

/*
  A legutóbb látott lista modul szinten. A foglaláslista azért jelenik meg azonnal, mert a
  feliratkozása az AdminLayout-ban él, és szekcióváltáskor nem bomlik le; a felhasználókat
  viszont csak ez az egy nézet kéri, ezért a feliratkozás vele együtt mountolódik. Ez a
  gyorstár adja ugyanazt a hatást felesleges olvasás nélkül: visszatéréskor az első
  képkockán ott az előző lista, a friss adatot a Firestore lokális cache-e hozza mögötte.
*/
let cachedUsers: User[] | null = null;

/**
 * A gyorstár ürítése kijelentkezéskor (`AuthContext`). A lista személyes adat (név, e-mail,
 * telefon), ezért közös gépen nem élheti túl a munkamenetet: a rendes kijelentkezés ugyan
 * újratölti az oldalt, a munkamenet viszont enélkül is elveszhet (lejáró jogosultság, másik
 * lapon történő kijelentkezés), és akkor a következő belépő első képkockáján még az előző
 * admin listája villanna fel, `loading: false` mellett.
 */
export function clearUsersCache() {
  cachedUsers = null;
}

/**
 * A users kollekció valós időben - a `useAdminBookings` párja az ügyféllistához. Egy
 * szerkesztés vagy törlés után nem kell újratölteni: a figyelő magától frissül.
 */
export function useUsers({ enabled = true }: { enabled?: boolean } = {}): UseUsersResult {
  const subscribe = useCallback<Subscribe<User[]>>(
    (onChange, onError) =>
      subscribeToUsers((users) => {
        cachedUsers = users;
        onChange(users);
      }, onError),
    [],
  );
  const { data, loading, error } = useSubscription(subscribe, {
    enabled,
    initialData: cachedUsers,
    errorMessage: "Hiba történt a felhasználók betöltése közben",
  });

  return { users: data ?? NO_USERS, loading, error };
}
